import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import dayjs from "dayjs";
import { toast } from "react-toastify";

export default function PatientAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("doctor.name");
  const [startIndex, setStartIndex] = useState(0);

  const daysWindow = [...Array(7)].map((_, i) => {
    const d = dayjs().add(startIndex + i, "day");
    return {
      label: d.format("ddd DD MMM"),
      value: d.format("YYYY-MM-DD"),
    };
  });


  // Generate next 7 days for header slider
  const next7Days = [...Array(7)].map((_, i) => {
    const d = dayjs().add(i, "day");
    return {
      label: d.format("ddd DD MMM"), // Fri 05 Dec
      value: d.format("YYYY-MM-DD"), // 2025-12-05
    };
  });

  useEffect(() => {
    axiosInstance.get("/appointment/patient").then((res) => {
      setAppointments(res.data);
    });
  }, []);

  // Filter appoints for selected date + search
  const filtered = appointments
    .filter((a: any) => a.date === selectedDate)
    .filter((a: any) =>
      a.doctor.name.toLowerCase().includes(search.toLowerCase()) ||
      a.doctor.specialisation.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a: any, b: any) => {
      const keys = sortKey.split(".");
      const aVal = keys.reduce((o, k) => o[k], a);
      const bVal = keys.reduce((o, k) => o[k], b);
      return aVal.toString().localeCompare(bVal.toString());
    });

  const confirmCancel = () => {
    return new Promise<boolean>((resolve) => {
      const confirmed = window.confirm("Are you sure you want to cancel the appointment?");
      resolve(confirmed);
    });
  };


  const cancelAppointment = async (id: number) => {
    const ok = window.confirm("Are you sure you want to cancel the appointment?");
    if (!ok) return;

    try {
      await axiosInstance.post("/appointment/cancel", { appointmentId: id });

      toast.success("Appointment cancelled successfully");

      // Reload the appointment list
      const res = await axiosInstance.get("/appointment/patient");
      setAppointments(res.data);

    } catch (err) {
      toast.error("Failed to cancel appointment");
    }
  }


  return (
    <div className="p-6 min-h-screen bg-gray-100">
      <h1 className="text-3xl font-bold mb-4 text-indigo-700">
        My Appointments
      </h1>

      {/* Search */}
      {/* <input
        type="text"
        placeholder="Search doctor..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full mb-4 px-3 py-2 border rounded-lg"
      /> */}

      {/* Sort */}
      {/* <select
        value={sortKey}
        onChange={(e) => setSortKey(e.target.value)}
        className="mb-4 px-3 py-2 border rounded-lg"
      >
        <option value="doctor.name">Sort by Name</option>
        <option value="doctor.specialisation">Sort by Specialisation</option>
      </select> */}

      {/* 7 Day Slider */}
      <div className="flex items-center gap-2 mb-5">
        <button
          disabled={startIndex === 0}
          onClick={() => setStartIndex((prev) => Math.max(prev - 7, 0))}
          className="px-3 py-2 bg-gray-200 rounded disabled:opacity-40"
        >
          ◀
        </button>

        <div className="flex gap-4 overflow-x-auto">
          {daysWindow.map((d) => (
            <button
              key={d.value}
              onClick={() => setSelectedDate(d.value)}
              className={`px-4 py-2 rounded-lg ${selectedDate === d.value
                ? "bg-indigo-600 text-white"
                : "bg-white border"
                }`}
            >
              {d.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setStartIndex((prev) => prev + 7)}
          className="px-3 py-2 bg-gray-200 rounded"
        >
          ▶
        </button>
      </div>


      {/* Appointments Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-indigo-600 text-white">
            <tr>
              <th className="p-3 text-left">Profile</th>
              <th className="p-3 text-left">Doctor</th>
              <th className="p-3 text-left">Specialisation</th>
              <th className="p-3 text-left">Treatments</th>
              <th className="p-3 text-left">Time</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-center text-gray-500">
                  No appointments on this day
                </td>
              </tr>
            ) : (
              filtered.map((appt: any) => (
                <tr key={appt.appointmentId} className="border-b">
                  <td className="p-3">
                    <img
                      src={appt.doctor.profile}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  </td>
                  <td className="p-3 font-medium">{appt.doctor.name}</td>
                  <td className="p-3">{appt.doctor.specialisation}</td>
                  <td className="p-3">
                    {appt.doctor.treatments?.join(", ")}
                  </td>
                  <td className="p-3">
                    {appt.start} – {appt.end}
                  </td>
                  <td className="p-3">
                    {appt.status === "CANCELLED" ? (
                      <span className="text-red-500 font-semibold">Cancelled</span>
                    ) : appt.status === "VIEWED" ? (
                      <span className="text-green-600 font-semibold">Viewed</span>
                    ) : appt.status === "BOOKED" && dayjs(appt.date).isBefore(dayjs(), "day") ? (
                      <span className="text-red-600 font-semibold">Missed</span>
                    ) : (
                      <button
                        onClick={() => cancelAppointment(appt.appointmentId)}
                        className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
                      >
                        Cancel
                      </button>
                    )}
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
